<template>
  <div class="tech-path-detail">
    <!-- 返回按钮 -->
    <div class="back-header">
      <el-button type="text" size="small" @click="back">
        <el-icon><ArrowLeft /></el-icon>
        返回分析结果
      </el-button>
    </div>
    
    <!-- 头部区域 -->
    <div class="detail-header">
      <div class="header-content">
        <h2 class="path-title">{{ techPath?.title || '技术路径详情' }}</h2>
        <p class="path-description">{{ techPath?.description || '暂无描述' }}</p>
        <div class="path-tag" v-if="techPath?.solutionDescription">
          <div class="solution-tag">{{ techPath.solutionDescription }}</div>
        </div>
        <div class="path-tags" v-if="techPath?.tags && techPath.tags.length > 0">
          <el-tag 
            v-for="(tag, index) in techPath.tags" 
            :key="index" 
            size="small" 
            class="tag-item"
          >
            {{ tag }}
          </el-tag>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="detail-content">
      <div v-if="detailLoading" class="detail-loading-state" role="status" aria-live="polite">
        <!-- 朴素进度指示：一条流动的细线 + 文字，不使用渐变球/跳动圆点等 AI 产品常见动效 -->
        <div class="detail-loading-bar" aria-hidden="true"><span></span></div>
        <h3>正在生成技术方案详情</h3>
        <p>模型正在梳理技术逻辑、结构方案和工程落地要点，请稍候…</p>
      </div>

      <template v-else>
      <!-- 优缺点分析 -->
      <div class="section" v-if="(techPath?.pros && techPath.pros.length > 0) || (techPath?.cons && techPath.cons.length > 0)">
        <div class="pros-cons-grid">
          <div class="pros-card" v-if="techPath?.pros && techPath.pros.length > 0">
            <h4 class="pros-title">优点</h4>
            <ul class="pros-list">
              <li v-for="(pro, index) in techPath.pros" :key="index" class="pros-item">{{ pro }}</li>
            </ul>
          </div>
          <div class="cons-card" v-if="techPath?.cons && techPath.cons.length > 0">
            <h4 class="cons-title">缺点/风险</h4>
            <ul class="cons-list">
              <li v-for="(con, index) in techPath.cons" :key="index" class="cons-item">{{ con }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 方案详情图片 -->
      <div class="section">
        <h3 class="section-title">方案详情</h3>
        <div v-if="imageLoading" class="image-loading-text">正在使用视觉模型生成方案示意图...</div>
        <div class="image-grid">
          <el-image
            v-for="(image, index) in techPath?.images || []"
            :key="index"
            :src="image"
            :alt="`方案示意图${index + 1}`"
            :preview-src-list="techPath?.images || []"
            :initial-index="index"
            fit="contain"
            preview-teleported
            class="detail-image"
          >
            <template #error>
              <div class="image-error">
                <el-icon><Picture /></el-icon>
              </div>
            </template>
          </el-image>
        </div>
        <el-empty v-if="!imageLoading && (!techPath?.images || techPath.images.length === 0)" description="暂无方案示意图" :image-size="72" />
      </div>

      <!-- 技术逻辑 -->
      <div class="section">
        <h3 class="section-title">技术逻辑</h3>
        <div class="logic-card">
          <p v-for="(para, index) in splitParagraphs(techPath?.technicalLogic || '暂无技术逻辑描述')" :key="index" class="logic-text">{{ para }}</p>
        </div>
      </div>

      <!-- 结构方案 -->
      <div class="section">
        <h3 class="section-title">结构方案</h3>
        <div class="structure-card">
          <p v-for="(para, index) in splitStructurePlan(techPath?.structurePlan || '暂无结构方案描述')" :key="index" class="structure-text">{{ para }}</p>
        </div>
      </div>

      <!-- 适用场景 -->
      <div class="section">
        <h3 class="section-title">适用场景</h3>
        <div class="scenario-card">
          <ul class="scenario-list">
            <li 
              v-for="(scenario, index) in techPath?.applicationScenarios || []" 
              :key="index" 
              class="scenario-item"
            >
              {{ scenario }}
            </li>
          </ul>
        </div>
      </div>

      <!-- 工程落地要点 -->
      <div class="section">
        <h3 class="section-title">工程落地要点</h3>
        <div class="engineering-card">
          <ol class="engineering-list">
            <li 
              v-for="(point, index) in techPath?.engineeringPoints || []" 
              :key="index" 
              class="engineering-item"
            >
              {{ point }}
            </li>
          </ol>
        </div>
      </div>

      <!-- 风险点与缓解措施 -->
      <div class="section">
        <h3 class="section-title">风险点与缓解措施</h3>
        <div class="risk-card">
          <ul class="risk-list">
            <li 
              v-for="(item, index) in techPath?.riskMitigations || []" 
              :key="index" 
              class="risk-item"
            >
              <strong>风险点：</strong>{{ item.risk }}<br>
              <strong>缓解措施：</strong>{{ item.mitigation }}
            </li>
          </ul>
        </div>
      </div>

      <!-- 应用的TRIZ原理 -->
      <div class="section">
        <h3 class="section-title">应用的TRIZ原理</h3>
        <div class="triz-grid">
          <el-collapse v-model="activeNames" accordion>
            <el-collapse-item 
              v-for="(principle, index) in techPath?.trizPrinciples || []" 
              :key="index" 
              :title="`${code+principle.number}`" 
              :name="(index + 1).toString()"
            >
              <div class="triz-content">
                <p v-for="(para, pi) in splitByNumber(principle.description)" :key="pi" class="triz-para">{{ para }}</p>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>

      <!-- 原理总结 -->
      <div class="section">
        <h3 class="section-title">原理总结</h3>
        <div class="summary-card">
          <p class="summary-text">{{ techPath?.principleSummary || '暂无原理总结描述' }}</p>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ArrowLeft, Picture } from '@element-plus/icons-vue'

// 定义组件接收的props
const props = defineProps({
  techPath: {
    type: Object,
    default: () => ({})
  },
  detailLoading: {
    type: Boolean,
    default: false
  },
  imageLoading: {
    type: Boolean,
    default: false
  }
})
const code = '#'

// 定义组件发射的事件
const emit = defineEmits(['back'])

// 返回按钮点击事件
const back = () => {
  emit('back')
}

// TRIZ原理折叠面板激活状态
const activeNames = ref(['1'])

// 长文本按序号切分成段落数组（兼容 \n、以及 "1." "2." 等序号），每个序号另起一段
const splitByNumber = (str) => {
  if (!str) return []
  let s = String(str)
  const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (lines.length > 1) return lines
  const parts = s.split(/(?=\d{1,2}[\.、．]\s*)/).map(p => p.trim()).filter(Boolean)
  return parts.length > 1 ? parts : (s ? [s] : [])
}

// 详情长文本按换行拆成段落数组（模型按要求输出至少4段，\n 由多个 <p> 渲染为独立段落）
const splitParagraphs = (str) => {
  if (!str) return []
  return String(str).split(/\r?\n+/).map(l => l.trim()).filter(Boolean)
}

// 结构方案：按换行分段后，再把段内"1.xxx；2.xxx"形式的编号步骤拆成多行（避免误拆 0.3mm 等数值）
const splitStructurePlan = (text) => {
  if (!text) return []
  return splitParagraphs(text).flatMap((para) => {
    if (!/[\s；：][1-9]\d?[\.、．]/.test(para)) return [para]
    const parts = para
      .split(/(?<=[\s；：])(?=[1-9]\d?[\.、．])/)
      .map(s => s.replace(/^[\s；：]+/, '').trim())
      .filter(Boolean)
    return parts.length > 1 ? parts : [para]
  })
}
</script>

<style scoped lang="scss">
.tech-path-detail {
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  min-height: 100%;
}

/* 返回按钮样式 */
.back-header {
  padding: 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  .el-button {
    color: #1890ff;
    font-size: 14px;
    padding: 0;
  }
}

/* 头部区域 */
.detail-header {
  background-color: #ffffff;
  padding: 30px 40px;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.header-content {
  // margin: 0 auto;
}

.path-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.path-description {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.path-tag {
  margin-top: 8px;
  margin-bottom: 12px;
}

.solution-tag {
  display: inline-block;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #52c41a;
  font-size: 13px;
  line-height: 1.6;
  border-radius: 4px;
  padding: 4px 10px;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.path-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  
  .tag-item {
    background-color: #f0f9ff;
    color: #1890ff;
    border-color: #90caf9;
  }
}

/* 主要内容区域 */
.detail-content {
  padding: 30px 0px;
  flex: 1;
  box-sizing: border-box;
}

.detail-loading-state {
  min-height: min(560px, calc(100vh - 240px));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(26, 42, 62, 0.08);
  box-sizing: border-box;

  h3 {
    margin: 4px 0 0;
    color: #303133;
    font-size: 20px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #909399;
    font-size: 14px;
    text-align: center;
  }
}

.detail-loading-bar {
  position: relative;
  width: 160px;
  height: 3px;
  border-radius: 2px;
  background: #eef1f5;
  overflow: hidden;

  span {
    position: absolute;
    top: 0;
    left: -40%;
    width: 40%;
    height: 100%;
    border-radius: 2px;
    background: #5b7fae;
    animation: detail-loading-slide 1.5s ease-in-out infinite;
  }
}

@keyframes detail-loading-slide {
  0% { left: -40%; }
  100% { left: 100%; }
}

/* 通用区块样式 */
.section {
  margin-bottom: 30px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  padding-bottom: 20px;
}

/* 方案详情图片 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.image-loading-text {
  margin: -4px 0 14px;
  color: #909399;
  font-size: 13px;
}

.detail-image {
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  overflow: hidden;
  min-width: 0;
  cursor: zoom-in;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  :deep(img) {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #c0c4cc;
  font-size: 32px;
}

/* 技术逻辑和结构方案 */
.logic-card,
.structure-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.logic-text,
.structure-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* 多段渲染时段落间留间距 */
.logic-text + .logic-text,
.structure-text + .structure-text {
  margin-top: 12px;
}

/* 适用场景 */
.scenario-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.scenario-list {
  margin: 0;
  padding-left: 20px;
}

.scenario-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

/* 工程落地要点 */
.engineering-card {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.engineering-list {
  margin: 0;
  padding-left: 20px;
}

.engineering-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

/* 风险点与缓解措施 */
.risk-card {
  background-color: #fff5f5;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 20px;
}

.risk-list {
  margin: 0;
  padding-left: 20px;
}

.risk-item {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    color: #f56c6c;
  }
}

/* 优缺点分析 */
.pros-cons-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.pros-card {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 20px;
}

.pros-title {
  font-size: 16px;
  font-weight: 600;
  color: #52c41a;
  margin: 0 0 12px 0;
}

.pros-list,
.cons-list {
  margin: 0;
  padding-left: 20px;
}

.pros-item,
.cons-item {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 8px;
}

.pros-item {
  color: #52c41a;
}

.cons-item {
  color: #fa8c16;
}

.cons-card {
  background-color: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 8px;
  padding: 20px;
}

.cons-title {
  font-size: 16px;
  font-weight: 600;
  color: #fa8c16;
  margin: 0 0 12px 0;
}

/* TRIZ原理 */
.triz-grid {
  margin-top: 16px;
}

:deep(.el-collapse) {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-collapse-item) {
  border-bottom: 1px solid #e8e8e8;
  
  &:last-child {
    border-bottom: none;
  }
}

:deep(.el-collapse-item__header) {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  &.is-active {
    background-color: #e6f7ff;
    border-bottom-color: #91d5ff;
  }
}

:deep(.el-collapse-item__content) {
  padding: 20px;
  background-color: #ffffff;
  border-bottom: none;
}

.triz-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  padding: 0 10px;

  .triz-para {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* 原理总结 */
.summary-card {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-text {
  font-size: 14px;
  color: #595959;
  line-height: 1.7;
  margin: 0;
  text-indent: 2em;
  
  &::before {
    content: "✓ ";
    color: #52c41a;
    font-weight: bold;
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .detail-header {
    padding: 20px;
  }
  
  .detail-content {
    padding: 20px;
  }
  
  .path-title {
    font-size: 20px;
  }
  
  .path-description {
    font-size: 14px;
  }
  
  .section {
    padding: 16px;
  }
  
  .section-title {
    font-size: 18px;
  }
  
  .image-grid {
    grid-template-columns: 1fr;
  }
  
  .pros-cons-grid {
    grid-template-columns: 1fr;
  }
}
</style>
